import React, { memo } from 'react';
import {
  Icon,
  Dropdown,
  ActionPopup,
  Input,
  Text
} from '@shootsta/common-react';
import { Shot, SCRIPT_TONE_OPTIONS, DragHandlers } from '../types';
import { customSelectStyles } from '../styles/dropdownStyles';
import { useSceneCardHandlers } from '../hooks/useSceneCardHandlers';
import FadedTextInput from './FadedTextInput';
import CollapsibleTextInput from './CollapsibleTextInput';
import MediaSection from '../containers/MediaSection';

const TONE_OPTIONS = SCRIPT_TONE_OPTIONS.filter((opt) => opt.value !== '').map(
  (option) => ({
    label: (
      <div className="flex items-center gap-2 text-[#515E71]">
        <Icon
          name={option.icon}
          size={14}
          color="slateBlueGray"
          className="pl-1"
        />
        {option.label}
      </div>
    ),
    value: option.value
  })
);

interface GridViewProps {
  shot: Shot;
  sceneIndex: number;
  hasUpdatePermission: boolean;
  hasDeletePermission: boolean;
  isDragging: boolean;
  cardStyles: React.CSSProperties;
  onUpdateShot: (updates: Partial<Shot>) => void;
  onDeleteShot: () => void;
  onDuplicateShot: () => void;
  onSaveShot: () => Promise<void>;
  addFileToQueue: (payload: any, orgId: string) => Promise<string>;
  organisationId: string;
  dragHandlers?: DragHandlers;
  commentCount?: number;
  onComment?: () => void;
  onSwitchToListView: () => void;
}

function GridView({
  shot,
  sceneIndex,
  hasUpdatePermission,
  hasDeletePermission,
  isDragging,
  cardStyles,
  onUpdateShot,
  onDeleteShot,
  onDuplicateShot,
  onSaveShot,
  addFileToQueue,
  organisationId,
  dragHandlers,
  commentCount,
  onComment,
  onSwitchToListView
}: GridViewProps) {
  const {
    sceneId,
    handleFieldBlur,
    handleScriptToneChange,
    handleTitleBlur,
    handleAssetChange,
    handleDragStart,
    moreActionsOptions
  } = useSceneCardHandlers({
    shot,
    onUpdateShot,
    onSaveShot,
    onDeleteShot,
    onDuplicateShot,
    hasDeletePermission,
    dragHandlers
  });

  const toneDropdownValue = TONE_OPTIONS.find(
    (option) => option.value === shot.scriptTone
  );

  return (
    <div
      ref={(cardRef) =>
        cardRef?.setAttribute('data-scene-card', String(sceneId))
      }
      className={`bg-white rounded-lg border border-russian overflow-hidden shadow-sm ${isDragging ? 'opacity-50' : ''}`}
      style={{ ...cardStyles }}
    >
      {/* Header Row */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-russian bg-sterling">
        {hasUpdatePermission && dragHandlers && (
          <div
            className="cursor-grab hover:bg-parthenon rounded p-0.5"
            draggable
            onDragStart={handleDragStart}
            onDragEnd={dragHandlers.onDragEnd}
          >
            <Icon name="dragindicator" size={18} color="regent" />
          </div>
        )}
        <span className="flex h-6 w-6 items-center justify-center rounded bg-darkDenim text-xs font-semibold text-white flex-shrink-0">
          {sceneIndex + 1}
        </span>
        <div className="flex-1 min-w-0 shot-v3__title-input">
          <Input
            defaultValue={shot.name || ''}
            onBlur={handleTitleBlur}
            disabled={!hasUpdatePermission}
            placeholder={`Scene ${sceneIndex + 1}`}
            className="h-8"
            id={`title_grid_${sceneIndex}`}
          />
        </div>
        {hasUpdatePermission && (
          <ActionPopup
            options={moreActionsOptions}
            popupBtn={<Icon name="options" size={14} color="regent" />}
          />
        )}
      </div>

      {/* Card Body */}
      <div className="p-4 flex flex-col gap-4">
        <MediaSection
          assetId={shot.assetId || ''}
          addFileToQueue={addFileToQueue}
          organisationId={organisationId}
          hasUpdatePermission={hasUpdatePermission}
          hasDeletePermission={hasDeletePermission}
          onAssetChange={handleAssetChange}
          onSave={onSaveShot}
          viewMode="grid"
          commentCount={commentCount}
          onComment={onComment}
        />

        {/* Script Section */}
        <div>
          <div className="flex items-center justify-between mb-2 mt-2">
            <div className="flex items-center gap-1.5">
              <Icon name="transcribe" size={16} />
              <Text label noMargin>
                Script
              </Text>
            </div>
            <div className="flex items-center gap-1 ml-auto cursor-pointer [&_.icon-arrowdown_path]:fill-[#515e71]">
              <Dropdown
                onChange={handleScriptToneChange}
                options={TONE_OPTIONS}
                value={toneDropdownValue}
                placeholder="Select tone"
                disabled={!hasUpdatePermission}
                selectStyles={customSelectStyles}
              />
            </div>
          </div>
          {hasUpdatePermission ? (
            <CollapsibleTextInput
              defaultValue={shot.script || ''}
              placeholder="E.g. Take your business to the next level with a simple, powerful tool...."
              onBlur={handleFieldBlur('script')}
              id={`script_grid_${sceneIndex}`}
            />
          ) : (
            <FadedTextInput
              defaultValue={shot.script || ''}
              placeholder="E.g. Take your business to the next level with a simple, powerful tool...."
              onSwitchToListView={onSwitchToListView}
            />
          )}
        </div>

        {/* Visuals Section */}
        <div>
          <div className="flex items-center gap-1.5 mb-2">
            <Icon name="inspiration" size={16} />
            <Text label noMargin>
              Visuals Description
            </Text>
          </div>
          {hasUpdatePermission ? (
            <CollapsibleTextInput
              defaultValue={shot.description || ''}
              placeholder="E.g. Shot of a user navigating the product, followed by a close-up of its key features...."
              onBlur={handleFieldBlur('description')}
              id={`description_grid_${sceneIndex}`}
            />
          ) : (
            <FadedTextInput
              defaultValue={shot.description || ''}
              placeholder="E.g. Shot of a user navigating the product, followed by a close-up of its key features...."
              onSwitchToListView={onSwitchToListView}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default memo(GridView);
